import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, CreditCard, Store, Copy, Landmark } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { SeatMap, priceMap } from "./SeatMap";
import { TicketQR } from "./TicketQR";
import { AuthForm } from "./AuthForm";
import { PaxFields, emptyPax, paxValid, paxList } from "./PaxFields";
import { FrequentChips, useFrequent, fillWith } from "./FrequentPassengers";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatARS } from "../../lib/site";
import { PaymentLogos, useCardEnabled } from "./PaymentLogos";
import { TransferBox, useTransfer } from "./TransferBox";

export const LEG_LABEL = { ida: "Ida", vuelta: "Vuelta" };
export const legTotal = (l) => l.seats.reduce((a, s) => a + (priceMap(l.trip)[l.cats[s]] ?? 0), 0);

/** Paso 1: elegir butacas de un servicio (tocar = elegir, tocar de nuevo = soltar). */
export const SeatDialog = ({ pick, onClose, onConfirm }) => {
  const [seats, setSeats] = useState([]);
  const [cats, setCats] = useState({});
  useEffect(() => { setSeats(pick?.initial ?? []); }, [pick]);
  const trip = pick?.trip;
  return (
    <Dialog open={Boolean(trip)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="booking-dialog" className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-3xl" data-lenis-prevent>
        {trip && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl">{LEG_LABEL[pick.leg]}: {trip.origin} → {trip.destination}</DialogTitle>
              <DialogDescription>{trip.date} · {trip.departure} hs · Elegí una o varias butacas</DialogDescription>
            </DialogHeader>
            <SeatMap trip={trip} selected={seats} onChange={setSeats} onCats={setCats} />
            <button disabled={!seats.length} onClick={() => onConfirm({ leg: pick.leg, trip, seats, cats })} data-testid="seats-continue-button"
              className="mt-2 h-12 w-full rounded-xl bg-[var(--forest)] font-bold text-white transition-opacity disabled:opacity-40">
              {seats.length ? `Continuar con ${seats.length} butaca${seats.length > 1 ? "s" : ""}` : "Elegí al menos una butaca"}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const LegBreakdown = ({ l }) => {
  const prices = priceMap(l.trip);
  return (
    <div data-testid={`breakdown-${l.leg}`} className="space-y-1">
      <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--forest)]">{LEG_LABEL[l.leg]} · {l.trip.origin} → {l.trip.destination}</p>
      {l.seats.map((s) => <div key={s} className="flex justify-between"><span>Butaca {s} · {l.cats[s]}</span><span>{formatARS(prices[l.cats[s]] ?? 0)}</span></div>)}
    </div>
  );
};

const Breakdown = ({ legs, promo }) => {
  const full = legs.reduce((a, l) => a + legTotal(l), 0);
  const off = (l) => (promo && (!promo.destinations.length || promo.destinations.includes(l.trip.destination))
    ? l.seats.reduce((a, s) => { const p = priceMap(l.trip)[l.cats[s]] ?? 0; return a + p - Math.round((p * (1 - promo.pct / 100)) / 100) * 100; }, 0) : 0);
  const discount = legs.reduce((a, l) => a + off(l), 0);
  return (
  <div data-testid="booking-breakdown" className="space-y-3 rounded-2xl bg-[var(--paper)] px-5 py-4 text-sm">
    {legs.map((l) => <LegBreakdown key={l.leg} l={l} />)}
    {discount > 0 && <div data-testid="booking-discount" className="flex justify-between font-semibold text-[var(--forest)]"><span>Descuento {promo.code} ({promo.pct}%)</span><span>−{formatARS(discount)}</span></div>}
    <div className="flex items-center justify-between border-t border-black/10 pt-2"><span className="font-semibold">Total</span><span data-testid="booking-total" className="font-display-up text-2xl text-[var(--forest)]">{formatARS(full - discount)}</span></div>
  </div>
  );
};

const Codes = ({ legs, codes, setCodes, promo, setPromo }) => {
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/promos/check", { params: { code: codes.promo, destinations: legs.map((l) => l.trip.destination).join("|") } });
      setPromo(data); toast.success(`Descuento de ${data.pct}% aplicado`);
    } catch (err) { setPromo(null); toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <div className="flex gap-2">
        <Input data-testid="promo-code-input" placeholder="Código de descuento" value={codes.promo} onChange={(e) => { setCodes({ ...codes, promo: e.target.value.toUpperCase() }); setPromo(null); }} className="h-11 rounded-xl uppercase" />
        <button type="button" onClick={apply} disabled={!codes.promo || busy} data-testid="promo-apply-button" className="h-11 shrink-0 rounded-xl border-2 border-[var(--forest)] px-4 text-sm font-bold text-[var(--forest)] disabled:opacity-40">{promo ? "Aplicado" : "Aplicar"}</button>
      </div>
      <Input data-testid="agency-code-input" placeholder="Código de agencia (opcional)" value={codes.agency} onChange={(e) => setCodes({ ...codes, agency: e.target.value.toUpperCase() })} className="h-11 rounded-xl uppercase" />
    </div>
  );
};

const copyFrom = (from, fromPax, to, toPax) => Object.fromEntries(to.seats.map((s, i) => [s, fromPax[from.seats[i]] ? { ...fromPax[from.seats[i]] } : toPax[s]]));

const LegPax = ({ l, pax, onChange, frequent, onCopy }) => (
  <div data-testid={`leg-pax-${l.leg}`} className="space-y-2 rounded-2xl border border-black/10 p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm font-bold">{LEG_LABEL[l.leg]} · {l.trip.from_stop} → {l.trip.to_stop} · {l.trip.date} {l.trip.departure} hs</p>
      {onCopy && <button type="button" onClick={onCopy} data-testid="copy-pax-from-ida" className="inline-flex items-center gap-1.5 rounded-full bg-[var(--paper)] px-3 py-1.5 text-xs font-semibold hover:bg-[var(--lime)]/50"><Copy className="h-3.5 w-3.5" /> Mismos pasajeros que la ida</button>}
    </div>
    <FrequentChips list={frequent} seats={l.seats} pax={pax} onPick={(p) => onChange(fillWith(l.seats, pax, p))} />
    <PaxFields seats={l.seats} pax={pax} onChange={onChange} labelOf={(s) => `Butaca ${s} · ${l.cats[s] ?? ""}`} testPrefix={l.leg === "ida" ? "pax" : "pax-vuelta"} />
  </div>
);

const PassengerForm = ({ legs, onDone }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name ?? "", dni: user?.dni ?? "", email: user?.email ?? "", phone: user?.phone ?? "" });
  const me = user ? { name: user.name ?? "", dni: user.dni ?? "" } : null;
  const [pax, setPax] = useState(() => Object.fromEntries(legs.map((l) => [l.leg, emptyPax(l.seats, {}, me)])));
  const [loading, setLoading] = useState("");
  const card = useCardEnabled();
  const transfer = useTransfer();
  const [terms, setTerms] = useState(false);
  const [codes, setCodes] = useState(() => ({ promo: "", agency: (new URLSearchParams(window.location.search).get("ag") || sessionStorage.getItem("sg_ag") || "").toUpperCase() }));
  const [promo, setPromo] = useState(null);
  const [frequent] = useFrequent();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const leg = (l) => ({ trip_id: l.trip.id, from_idx: l.trip.from_idx, to_idx: l.trip.to_idx, seats: l.seats, pax: paxList(l.seats, pax[l.leg]) });
  const canCard = card;
  const extra = (mode) => ({ ...form, accepted_terms: terms, promo_code: promo ? promo.code : "", agency_code: codes.agency.trim(), pay_by: mode === "transfer" ? "transferencia" : "boleteria", legs: legs.map(leg) });

  const go = async (mode) => {
    setLoading(mode);
    try {
      if (mode === "card") {
        const { data } = await api.post("/payments/nave/checkout-group", extra(mode));
        window.location.assign(data.checkout_url);
        return;
      }
      const { data } = await api.post("/bookings/group", extra(mode));
      onDone(Array.isArray(data?.bookings) ? data.bookings : []);
      toast.success(mode === "transfer" ? "¡Reserva registrada! Ahora hacé la transferencia" : legs.length > 1 ? "¡Ida y vuelta reservadas!" : "¡Reserva registrada!");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(""); }
  };
  const valid = terms && form.name.length >= 2 && form.dni.length >= 6 && form.email.includes("@") && legs.every((l) => paxValid(l.seats, pax[l.leg]));
  const ida = legs.find((l) => l.leg === "ida");

  return (
    <form onSubmit={(e) => { e.preventDefault(); go(canCard ? "card" : "reserve"); }} className="space-y-3">
      <p className="text-xs text-[var(--muted-ink)]">Titular de la compra (cuenta {user?.email}). Te llegan los pasajes y avisos a este email.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input data-testid="booking-name-input" placeholder="Titular: nombre y apellido" value={form.name} onChange={set("name")} required className="h-11 rounded-xl" />
        <Input data-testid="booking-dni-input" placeholder="DNI del titular" inputMode="numeric" value={form.dni} onChange={set("dni")} required minLength={6} maxLength={10} className="h-11 rounded-xl" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input data-testid="booking-email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} required className="h-11 rounded-xl" />
        <Input data-testid="booking-phone-input" type="tel" inputMode="tel" maxLength={30} placeholder="Celular con WhatsApp (opcional)" value={form.phone} onChange={set("phone")} className="h-11 rounded-xl" />
      </div>
      <p className="text-xs text-[var(--muted-ink)]">Con tu celular te avisamos por WhatsApp la confirmación, cambios de horario y un recordatorio el día antes. Ej.: 11 2345-6789.</p>
      <p className="pt-1 text-sm font-bold">Quién viaja en cada butaca</p>
      {legs.map((l) => (
        <LegPax key={l.leg} l={l} pax={pax[l.leg]} frequent={frequent} onChange={(p) => setPax({ ...pax, [l.leg]: p })}
          onCopy={l.leg === "vuelta" && ida ? () => setPax({ ...pax, vuelta: copyFrom(ida, pax.ida, l, pax.vuelta) }) : null} />
      ))}
      <Codes legs={legs} codes={codes} setCodes={setCodes} promo={promo} setPromo={setPromo} />
      <Breakdown legs={legs} promo={promo} />
      <label data-testid="terms-accept-label" className="flex items-start gap-3 rounded-2xl border border-black/10 p-4 text-sm">
        <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="terms-accept-checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--forest)]" />
        <span>Leí y acepto los <Link to="/terminos-y-condiciones" target="_blank" data-testid="terms-link" className="font-bold text-[var(--forest)] underline">términos y condiciones</Link> y la <Link to="/cambios-y-devoluciones" target="_blank" className="font-bold text-[var(--forest)] underline">política de cambios y devoluciones</Link>.</span>
      </label>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {canCard && <button type="submit" disabled={!valid || !!loading} data-testid="booking-pay-card-button" className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[var(--forest)] font-bold text-white hover:bg-[var(--forest-2)] disabled:opacity-40">{loading === "card" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pagar con tarjeta</button>}
        {transfer && <button type="button" onClick={() => go("transfer")} disabled={!valid || !!loading} data-testid="booking-pay-transfer-button" className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-xl font-bold disabled:opacity-40 ${canCard ? "border-2 border-[var(--forest)] text-[var(--forest)]" : "bg-[var(--forest)] text-white hover:bg-[var(--forest-2)]"}`}>{loading === "transfer" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />} Pagar por transferencia</button>}
        <button type={canCard ? "button" : "submit"} onClick={canCard ? () => go("reserve") : undefined} disabled={!valid || !!loading} data-testid="booking-confirm-button" className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-xl font-bold disabled:opacity-40 ${canCard || transfer ? "border-2 border-[var(--forest)] text-[var(--forest)]" : "bg-[var(--forest)] text-white hover:bg-[var(--forest-2)]"}`}>
          {loading === "reserve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Store className="h-4 w-4" />} {legs.length > 1 ? "Reservar ida y vuelta" : "Reservar y pagar en boletería"}
        </button>
      </div>
      {canCard && <PaymentLogos testid="checkout-payment-logos" className="justify-center" />}
      <p className="text-center text-xs text-[var(--muted-ink)]">{canCard ? `Con tarjeta pagás online por NAVE (Banco Galicia)${legs.length > 1 ? " la ida y la vuelta en un solo pago" : ""} y tus butacas quedan apartadas 15 minutos. ` : ""}{legs.length > 1 ? "Se reservan los dos tramos juntos, con un código para cada uno. " : ""}{transfer ? "Por transferencia te damos el alias y el CBU al reservar; el pasaje se confirma cuando se acredita. " : ""}Si reservás, pagás en cualquier boletería hasta 2 horas antes de la salida; si no, la reserva vence.</p>
    </form>
  );
};

const fmtExp = (s) => (s ? new Date(s).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Argentina/Buenos_Aires" }) : "");

const Ticket = ({ b, n }) => (
  <div data-testid={`booking-ticket-${n}`} className="rounded-2xl bg-[var(--paper)] p-4">
    <p className="font-semibold">{b.origin} → {b.destination} · {b.date} {b.departure} hs</p>
    <p className="mt-1 text-sm text-[var(--muted-ink)]">Butacas {(b.seats ?? []).join(", ")} · {formatARS(b.total)}</p>
    <p data-testid={n === 0 ? "booking-code" : `booking-code-${n}`} className="mt-2 font-mono text-2xl font-semibold tracking-[0.2em] text-[var(--forest)]">{b.code}</p>
    <TicketQR code={b.code} className="mt-3 ring-1 ring-black/10" />
    {b.ticket_url && <a href={b.ticket_url} target="_blank" rel="noreferrer" data-testid={n === 0 ? "booking-save-phone" : `booking-save-phone-${n}`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--lime)] px-5 py-2.5 text-sm font-bold text-[var(--ink)]">Guardar pasaje en el celular</a>}
    {b.pay_intent === "transferencia" && b.status === "reservado" ? <div className="mt-3"><TransferBox b={b} testid={n === 0 ? "booking-transfer" : `booking-transfer-${n}`} /></div>
      : b.expires_at && <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">Pagalo en cualquier boletería antes del <b>{fmtExp(b.expires_at)} hs</b>. Si no, la reserva vence.</p>}
  </div>
);

const Success = ({ done }) => (
  <div data-testid="booking-success" className="space-y-4 py-2 text-center">
    <CheckCircle2 className="mx-auto h-14 w-14 text-[var(--forest)]" />
    <p className="font-display text-3xl">¡Listo, {(done[0]?.name ?? "").split(" ")[0]}!</p>
    <p className="text-sm text-[var(--muted-ink)]">{done.length > 1 ? "Un código y un QR por tramo. Mostralos al conductor al subir." : "Mostrá este QR al conductor al subir."}</p>
    {done.map((b, n) => <Ticket key={b.code} b={b} n={n} />)}
    <p className="text-sm text-[var(--muted-ink)]">Te enviamos la confirmación a <b>{done[0]?.email}</b>. La tenés siempre en «Mi cuenta».</p>
  </div>
);

/** Paso 2: titular, pasajero de cada butaca (ida y vuelta) y confirmación de todo junto. */
export const CheckoutDialog = ({ legs, open, onClose, onBooked }) => {
  const { user } = useAuth();
  const [done, setDone] = useState(null);
  useEffect(() => { if (open) setDone(null); }, [open]);
  const finish = (list) => { setDone(list); onBooked(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkout-dialog" className="max-h-[92vh] max-w-xl overflow-y-auto rounded-3xl" data-lenis-prevent>
        {!done && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl">{legs.length > 1 ? "Tu ida y vuelta" : "Tu viaje"}</DialogTitle>
              <DialogDescription>{user ? "Datos del titular y de cada pasajero" : "Ingresá o creá tu cuenta"}</DialogDescription>
            </DialogHeader>
            {user ? <PassengerForm legs={legs} onDone={finish} /> : <AuthForm intro="Para reservar o comprar necesitás una cuenta. Tus butacas siguen elegidas." onDone={() => {}} />}
          </>
        )}
        {done && <Success done={done} />}
      </DialogContent>
    </Dialog>
  );
};
